import type {Shop, UiSettings} from '@myparcel-frontend/types';
import {useSdk} from '@myparcel-frontend/sdk';
import {storeToRefs} from 'pinia';
import useAcl from './useAcl';
import {useConfigStore} from '../store/config';

/**
 * Helper hook to manage UI settings from the API to the store.
 * Additional methods:
 * - getUiSettings: get the UI settings from the API, and put it in the store.
 * - putUiSettings: put the UI settings in the API and in the store.
 */
export default function useUiSettings() {
  const configStore = useConfigStore();
  const refs = storeToRefs(configStore);
  const {acl} = useAcl();

  const getUiSettings = async (): Promise<UiSettings | undefined> => {
    if (Object.keys(configStore.uiSettings).length) {
      return;
    }

    const {sdk} = useSdk();
    const uiSettings = await sdk.getUiSettings({
      path: {account_ids: acl.value.account_id},
    });

    const currentSettings = uiSettings.find((settings) => {
      return settings.user_id === acl.value.user_id;
    });

    if (!currentSettings) {
      return;
    }

    configStore.setUiSettings(currentSettings);
    return currentSettings;
  };

  const putUiSettings = async (uiSettingsData: Partial<UiSettings>): Promise<void> => {
    const {sdk} = useSdk();
    const result = await sdk.putUiSettings({body: [uiSettingsData]});
    configStore.setUiSettings(result[0]);
  };

  const setScanMode = (value: boolean): Promise<void> => putUiSettings({...configStore.uiSettings, scan_mode: value});

  const toggleScanMode = (): Promise<void> => setScanMode(!configStore.uiSettings.scan_mode);

  const setPreferredPrinterGroupId = (id?: string): Promise<void> =>
    putUiSettings({...configStore.uiSettings, preferred_printer_group_id: id || undefined});

  const ensurePreferredShop = async (settings: UiSettings, shops: Shop[]): Promise<UiSettings> => {
    if (settings.preferred_shop_id) {
      return settings;
    }

    const completedShopSettings = {...settings, preferred_shop_id: shops[0]?.id};

    try {
      await putUiSettings(completedShopSettings);
    } catch {
      configStore.setUiSettings(completedShopSettings);
    }

    return completedShopSettings;
  };

  return {
    putUiSettings,
    getUiSettings,
    ensurePreferredShop,
    setScanMode,
    toggleScanMode,
    setPreferredPrinterGroupId,
    uiSettings: refs.uiSettings,
    accountFetchError: refs.accountFetchError,
  };
}
